// Usage events to /api/analytics/collect (docs/analytics.md): batched, with active seconds, flushed on pagehide.
// Only client-side facts go here (screens, sounds, Baby Button, cry detection); the server logs its own actions.
type Props = Record<string, string | number | boolean | null>;
type Ev = { name: string; section?: string; objectId?: string; props?: Props; occurredAt: string };

let queue: Ev[] = [];
let vid: string | null = null;
let active = 0;
let lastInteraction = Date.now();
let enabled = false;

export const visitId = () => vid;

/** Events from before startAnalytics() (the first screen renders a moment earlier); sent once it starts. */
let early: Ev[] = [];

export function track(name: string, props?: Props, section?: string, objectId?: string) {
  const ev = { name, props, section, objectId, occurredAt: new Date().toISOString() };
  if (!enabled) {
    if (early.length < 30) early.push(ev);
    return;
  }
  queue.push(ev);
  if (queue.length >= 20) void flush();
}

// ---------- screens: page_view on arrival, screen_time (visible seconds) on leaving

let screenPath: string | null = null;
let screenMs = 0;
let screenSince = 0;

function screenSeconds() {
  return Math.round((screenMs + (document.visibilityState === 'visible' && screenSince ? Date.now() - screenSince : 0)) / 1000);
}

function leaveScreen() {
  if (!screenPath) return;
  const seconds = screenSeconds();
  if (seconds >= 1) track('screen_time', { path: screenPath, seconds });
  screenPath = null;
}

export function pageView(path: string, section?: string) {
  if (path === screenPath) return;
  leaveScreen();
  screenPath = path;
  screenMs = 0;
  screenSince = Date.now();
  track('page_view', { path }, section);
}

// ---------- taps: one ui_click per button, link or tab (never what is typed)

let maskWords: string[] = [];
/** Names to hide in tap labels ("Manage Mia" → "Manage {name}"): babies, family members, the parent. */
export function setMaskWords(words: (string | null | undefined)[]) {
  maskWords = words.filter((w): w is string => !!w && w.trim().length > 1).map((w) => w.trim());
}

function label(el: HTMLElement): string {
  // The first visible line: a row's label without its grey sub-line ("Send feedback", not "Send feedbackIdeas…").
  const firstLine = (el.innerText || el.textContent || '').split(/\n/).map((x) => x.trim()).find(Boolean);
  const raw = el.dataset.track || el.getAttribute('aria-label') || firstLine || el.getAttribute('title') || el.tagName.toLowerCase();
  let s = raw.replace(/\s+/g, ' ').trim();
  for (const w of maskWords) s = s.split(w).join('{name}');
  s = s.replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '{email}');
  return s.slice(0, 60) || el.tagName.toLowerCase();
}

function onClick(e: MouseEvent) {
  const t = e.target as Element | null;
  const el = t?.closest?.('[data-track], button, a, [role="button"], [role="tab"], [role="link"], summary') as HTMLElement | null;
  if (!el || el.closest('[data-track-off]')) return;
  const kind = el.getAttribute('role') || el.tagName.toLowerCase();
  track('ui_click', { path: location.pathname, kind }, undefined, label(el));
}

// ---------- errors: uncaught errors and promise rejections (a few per visit, no duplicates)

const seenErrors = new Set<string>();
export function reportError(kind: 'error' | 'rejection' | 'render', message: string, where?: string) {
  const key = `${kind}:${message}`;
  if (seenErrors.has(key) || seenErrors.size >= 10) return;
  seenErrors.add(key);
  track('js_error', { kind, message: message.slice(0, 200), where: (where ?? '').slice(0, 120), path: location.pathname });
}

function payload() {
  const standalone = matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  const out = JSON.stringify({
    visitId: vid ?? undefined,
    path: location.pathname,
    standalone,
    activeSeconds: Math.min(90, Math.round(active)),
    events: queue.splice(0, 50),
  });
  active = 0;
  return out;
}

export async function flush(beacon = false) {
  if (!enabled || (!queue.length && active < 1)) return;
  const body = payload();
  if (beacon && navigator.sendBeacon) {
    navigator.sendBeacon('/api/analytics/collect', new Blob([body], { type: 'text/plain' }));
    return;
  }
  try {
    const r = await fetch('/api/analytics/collect', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, credentials: 'same-origin' });
    if (r.ok) {
      const j = (await r.json().catch(() => null)) as { visitId?: string } | null;
      if (j?.visitId) vid = j.visitId;
    }
  } catch {
    /* offline: analytics events are dropped, which is acceptable */
  }
}

/** Start after sign-in. Active seconds: page visible and an interaction in the last 2 minutes. */
export function startAnalytics() {
  if (enabled) return;
  enabled = true;
  const now = new Date();
  const standalone = matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  const from = new URLSearchParams(location.search).get('from') === 'push' ? 'push' : 'direct';
  track('app_open', { standalone, hour: now.getHours(), weekday: now.getDay(), from, path: location.pathname });
  // The server accepts client times up to 60 s back; older early events are stamped "now" there.
  queue.push(...early);
  early = [];
  let hiddenAt = 0;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      if (screenSince) { screenMs += Date.now() - screenSince; screenSince = 0; }
    } else {
      screenSince = Date.now();
      const away = hiddenAt ? (Date.now() - hiddenAt) / 60_000 : 0;
      if (away >= 1) track('app_resume', { away_min: Math.round(away), hour: new Date().getHours(), path: location.pathname });
      hiddenAt = 0;
    }
  });
  addEventListener('click', onClick, { capture: true, passive: true });
  addEventListener('error', (e) => reportError('error', e.message || 'error', e.filename ? `${e.filename.split('/').pop()}:${e.lineno}` : ''));
  addEventListener('unhandledrejection', (e) => {
    const r = e.reason as { message?: string; name?: string } | undefined;
    // Network drops are expected offline; only real code failures are worth a look.
    if ((r && typeof r === 'object' && 'status' in r) || /fetch|network|load failed/i.test(r?.message ?? '')) return; // ApiError: an answered request, not a bug
    reportError('rejection', r?.message ?? String(e.reason));
  });
  const touch = () => { lastInteraction = Date.now(); };
  ['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach((e) => addEventListener(e, touch, { passive: true, capture: true }));
  setInterval(() => {
    if (document.visibilityState === 'visible' && Date.now() - lastInteraction < 120_000) active += 5;
  }, 5000);
  setInterval(() => void flush(), 30_000);
  addEventListener('pagehide', () => { leaveScreen(); void flush(true); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
}
